/**
 * Dev Handoffs (TRE-987) — the read surface for the TRE-980 beta feedback loop.
 *
 * Grafana trends the loop in aggregate; this screen reads ONE report in full so a
 * maintainer can triage it. Master/detail:
 *   - left: a filterable queue of dev_handoffs (triage state / trigger class / cluster).
 *   - right: the selected report. Front-and-center is the Decision-summary table
 *     built from the structured dev_handoff_gaps rows (tier + gap_type verdicts a
 *     maintainer acts on at a glance), then the full sanitized report_markdown
 *     rendered end-to-end below it.
 *
 * Triage affordances write through the service-role read API: set the handoff's
 * triage_status / backlog_issue_key, and link an individual gap to a backlog issue.
 * Every read treats the server response as truth (thin-client §9) and re-reads after
 * a mutation. Gated by `manage_trash` (admin/owner) — the nav tab only appears then.
 */

import { useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, ApiError } from "../api/client";
import type {
  AccountContext,
  HandoffDetail,
  HandoffGap,
  HandoffSummary,
  TriageStatus,
} from "../api/types";
import { TRIAGE_STATUSES, TRIGGER_SIGNAL_CLASSES } from "../api/types";
import { useToast } from "../components/Toast";
import { useAsyncData } from "../lib/useAsyncData";

const PAGE_SIZE = 50;

interface Filters {
  triageStatus: string;
  triggerSignalClass: string;
  clusterKey: string;
}
const EMPTY_FILTERS: Filters = { triageStatus: "", triggerSignalClass: "", clusterKey: "" };

export function Handoffs({ ctx: _ctx }: { ctx: AccountContext }) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useAsyncData(
    () =>
      api.handoffs({
        triageStatus: filters.triageStatus || undefined,
        triggerSignalClass: filters.triggerSignalClass || undefined,
        clusterKey: filters.clusterKey || undefined,
        limit: PAGE_SIZE,
      }),
    [filters.triageStatus, filters.triggerSignalClass, filters.clusterKey],
  );

  const unconfigured = list.error?.includes("503") || false;

  return (
    <div>
      <h1>Dev Handoffs</h1>
      <p className="screen-intro">
        Structured feedback reports from beta sessions that hit a real problem. Pick one to read it in
        full — the Decision-summary table up top is what to act on; the report below is the intent.
      </p>

      <FilterBar filters={filters} setFilters={setFilters} disabled={list.loading} />

      {list.loading && <p className="muted">Loading handoffs…</p>}
      {list.error && !unconfigured && (
        <div className="banner banner--error" role="alert">
          {list.error} <button className="btn--link" onClick={list.reload}>Retry</button>
        </div>
      )}
      {unconfigured && (
        <div className="banner banner--info">
          The dev-handoff sink isn't configured on this server yet (no Supabase read credential). Once
          it's wired up, reports land here.
        </div>
      )}

      {list.value && (
        <div className="handoff-layout">
          <HandoffList
            rows={list.value.handoffs}
            total={list.value.total}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          <div className="handoff-detail-pane">
            {selectedId ? (
              <HandoffReport
                key={selectedId}
                handoffId={selectedId}
                onTriaged={() => list.reload()}
              />
            ) : (
              <div className="handoff-empty muted">Select a report to read it in full.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterBar({
  filters,
  setFilters,
  disabled,
}: {
  filters: Filters;
  setFilters: (f: Filters) => void;
  disabled: boolean;
}) {
  return (
    <div className="handoff-filters" role="search">
      <label>
        <span className="muted">Triage</span>
        <select
          value={filters.triageStatus}
          disabled={disabled}
          onChange={(e) => setFilters({ ...filters, triageStatus: e.target.value })}
        >
          <option value="">All</option>
          {TRIAGE_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>
      <label>
        <span className="muted">Trigger</span>
        <select
          value={filters.triggerSignalClass}
          disabled={disabled}
          onChange={(e) => setFilters({ ...filters, triggerSignalClass: e.target.value })}
        >
          <option value="">All</option>
          {TRIGGER_SIGNAL_CLASSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>
      <label>
        <span className="muted">Cluster</span>
        <input
          type="text"
          value={filters.clusterKey}
          placeholder="cluster key…"
          disabled={disabled}
          onChange={(e) => setFilters({ ...filters, clusterKey: e.target.value })}
        />
      </label>
      {(filters.triageStatus || filters.triggerSignalClass || filters.clusterKey) && (
        <button className="btn--link" onClick={() => setFilters(EMPTY_FILTERS)}>
          Clear filters
        </button>
      )}
    </div>
  );
}

function HandoffList({
  rows,
  total,
  selectedId,
  onSelect,
}: {
  rows: HandoffSummary[];
  total: number | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (rows.length === 0) {
    return <div className="handoff-list handoff-list--empty banner banner--info">No handoffs match.</div>;
  }
  return (
    <div className="handoff-list" role="listbox" aria-label="Handoff reports">
      <div className="handoff-list-count muted">
        {rows.length}{total != null && total > rows.length ? ` of ${total}` : ""} report{rows.length === 1 ? "" : "s"}
      </div>
      {rows.map((h) => (
        <button
          key={h.handoff_id}
          role="option"
          aria-selected={selectedId === h.handoff_id}
          className={`handoff-row${selectedId === h.handoff_id ? " handoff-row--active" : ""}`}
          onClick={() => onSelect(h.handoff_id)}
        >
          <div className="handoff-row-top">
            <span className="handoff-row-subject">{h.subject ?? "(untitled report)"}</span>
            <TriageChip status={h.triage_status} />
          </div>
          <div className="handoff-row-meta muted">
            {h.trigger_signal_class && <span className="chip chip--trigger">{h.trigger_signal_class}</span>}
            <span>{h.gap_count} gap{h.gap_count === 1 ? "" : "s"}</span>
            <span>{formatDate(h.created_at)}</span>
            {h.backlog_issue_key && <span className="mono">{h.backlog_issue_key}</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function HandoffReport({ handoffId, onTriaged }: { handoffId: string; onTriaged: () => void }) {
  const detail = useAsyncData(() => api.handoff(handoffId).then((r) => r.handoff), [handoffId]);

  if (detail.loading) return <p className="muted">Loading report…</p>;
  if (detail.error) {
    return (
      <div className="banner banner--error" role="alert">
        {detail.error} <button className="btn--link" onClick={detail.reload}>Retry</button>
      </div>
    );
  }
  if (!detail.value) return <div className="muted">Report not found.</div>;
  const h = detail.value;

  return (
    <article className="handoff-report">
      <header className="handoff-report-head">
        <h2>{h.subject ?? "(untitled report)"}</h2>
        <dl className="handoff-report-facts">
          <Fact label="Triage" value={<TriageChip status={h.triage_status} />} />
          <Fact label="Trigger" value={h.trigger_signal_class ?? "—"} />
          <Fact label="Cluster" value={h.cluster_key ?? "—"} mono />
          <Fact label="Session" value={h.session_id ?? "—"} mono />
          <Fact label="Scrub" value={h.scrub_status} />
          <Fact label="Revit" value={h.revit_version ?? "—"} />
          <Fact label="Bridge" value={h.bridge_version ?? "—"} />
          <Fact label="Connector" value={h.connector_version ?? "—"} />
          <Fact label="Created" value={formatDate(h.created_at)} />
        </dl>
      </header>

      <TriagePanel handoff={h} onDone={() => { detail.reload(); onTriaged(); }} />

      <section aria-labelledby="decision-summary-h">
        <h3 id="decision-summary-h">Decision summary</h3>
        <DecisionTable handoff={h} onLinked={() => { detail.reload(); onTriaged(); }} />
      </section>

      <section aria-labelledby="full-report-h">
        <h3 id="full-report-h">Full report</h3>
        <div className="handoff-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{h.report_markdown}</ReactMarkdown>
        </div>
      </section>
    </article>
  );
}

function DecisionTable({ handoff, onLinked }: { handoff: HandoffDetail; onLinked: () => void }) {
  if (handoff.gaps.length === 0) {
    return <div className="banner banner--info">This report extracted no decision rows.</div>;
  }
  return (
    <table className="table handoff-decision">
      <thead>
        <tr>
          <th>#</th>
          <th>Gap</th>
          <th>Layer</th>
          <th>Type</th>
          <th>Proposal</th>
          <th>Owner</th>
          <th>Backlog</th>
        </tr>
      </thead>
      <tbody>
        {handoff.gaps.map((g) => (
          <GapRow key={g.gap_id} handoffId={handoff.handoff_id} gap={g} onLinked={onLinked} />
        ))}
      </tbody>
    </table>
  );
}

function GapRow({ handoffId, gap, onLinked }: { handoffId: string; gap: HandoffGap; onLinked: () => void }) {
  const [editing, setEditing] = useState(false);
  const [key, setKey] = useState(gap.backlog_issue_key ?? "");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    setBusy(true);
    try {
      await api.linkHandoffGap(handoffId, gap.gap_id, key.trim() || null);
      toast.push(key.trim() ? `Linked gap to ${key.trim()}.` : "Cleared gap link.");
      setEditing(false);
      onLinked();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "Couldn't link the gap.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td className="muted">{gap.ordinal ?? "—"}</td>
      <td>{gap.gap_summary ?? "—"}</td>
      <td><LayerBadge layer={gap.layer} raw={gap.layer_raw} /></td>
      <td><GapTypeBadge type={gap.gap_type} /></td>
      <td>{gap.proposal ?? "—"}</td>
      <td className="muted">{gap.owner ?? "—"}</td>
      <td className="handoff-gap-backlog">
        {editing ? (
          <span className="handoff-inline-edit">
            <input
              type="text"
              value={key}
              placeholder="TRE-…"
              disabled={busy}
              onChange={(e) => setKey(e.target.value)}
            />
            <button className="btn btn--small" disabled={busy} onClick={save}>Save</button>
            <button className="btn--link" disabled={busy} onClick={() => { setEditing(false); setKey(gap.backlog_issue_key ?? ""); }}>Cancel</button>
          </span>
        ) : gap.backlog_issue_key ? (
          <button className="btn--link mono" onClick={() => setEditing(true)}>{gap.backlog_issue_key}</button>
        ) : (
          <button className="btn--link" onClick={() => setEditing(true)}>Link…</button>
        )}
      </td>
    </tr>
  );
}

function TriagePanel({ handoff, onDone }: { handoff: HandoffDetail; onDone: () => void }) {
  const [status, setStatus] = useState<TriageStatus>(handoff.triage_status as TriageStatus);
  const [backlog, setBacklog] = useState(handoff.backlog_issue_key ?? "");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const dirty = status !== handoff.triage_status || (backlog.trim() || null) !== (handoff.backlog_issue_key ?? null);

  async function save() {
    setBusy(true);
    try {
      await api.updateHandoffTriage(handoff.handoff_id, {
        triage_status: status,
        backlog_issue_key: backlog.trim() || null,
      });
      toast.push("Triage saved.");
      onDone();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "Couldn't save triage.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="handoff-triage-panel">
      <label>
        <span className="muted">Triage status</span>
        <select value={status} disabled={busy} onChange={(e) => setStatus(e.target.value as TriageStatus)}>
          {TRIAGE_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>
      <label>
        <span className="muted">Backlog issue</span>
        <input
          type="text"
          value={backlog}
          placeholder="TRE-… (whole report)"
          disabled={busy}
          onChange={(e) => setBacklog(e.target.value)}
        />
      </label>
      <button className="btn" disabled={!dirty || busy} onClick={save}>Save triage</button>
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="handoff-fact">
      <dt className="muted">{label}</dt>
      <dd className={mono ? "mono" : undefined}>{value}</dd>
    </div>
  );
}

function TriageChip({ status }: { status: string }) {
  return <span className={`chip chip--triage-${status}`} data-glyph={TRIAGE_GLYPH[status] ?? "•"}>{status}</span>;
}
const TRIAGE_GLYPH: Record<string, string> = { new: "◆", triaged: "◐", linked: "↗", dismissed: "×" };

function LayerBadge({ layer, raw }: { layer: string | null; raw: string | null }) {
  if (!layer) return <span className="muted">{raw ?? "—"}</span>;
  return <span className={`chip chip--layer-${layer}`} title={raw ?? undefined}>{LAYER_LABEL[layer] ?? layer}</span>;
}
const LAYER_LABEL: Record<string, string> = {
  "add-in-1a": "Add-in 1a",
  "server-1b": "Server 1b",
  "skill-2": "Skill 2",
  "client-3": "Client 3",
  mixed: "Mixed",
};

function GapTypeBadge({ type }: { type: string | null }) {
  if (!type) return <span className="muted">—</span>;
  return <span className={`chip chip--gaptype-${type.replace(/[^a-z]/gi, "-")}`}>{type}</span>;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}
